import type { AnyLineTax, LineTax } from "components/SalesOrderDetails/types";

export const f2 = (v: any) => Math.round((Number(v) || 0) * 100) / 100;
export const f4 = (v: any) => Math.round((Number(v) || 0) * 10000) / 10000;

export function coerceToLineTax(
  entry: AnyLineTax | undefined,
  qty: number,
  unit: number,
  appliedState: string
): LineTax {
  const total = Number(entry?.total_product_tax_amount ?? entry?.amount ?? 0);
  const pct = Number(entry?.tax_percentage ?? ((entry?.rate ?? 0) * 100));
  const unitTax = qty > 0 ? total / qty : 0;

  return {
    tax_percentage: f2(pct),
    unit_product_tax_amount: f2(unitTax),
    total_product_tax_amount: f2(total),
    taxable: Boolean(entry?.taxable ?? (pct > 0 || total > 0)),
    tax_details: {
      combined_rate: f2(entry?.tax_details?.combined_rate ?? pct),
      tax_amount: f2(entry?.tax_details?.tax_amount ?? total),
      applied_state: entry?.tax_details?.applied_state ?? appliedState,
    },
  };
}

export const getItemTinfo = (item: any, liveMap: Record<string, { amount: number; rate: number }>) => {
  const liveKey = String(item?.sku || item?.itemId || item?.product_id || "");
  const live = liveMap?.[liveKey];
  if (live) return { amount: Number(live.amount || 0), rate: Number(live.rate || 0) };

  const iti = item?.item_tax_info;
  if (iti) {
    return {
      amount: Number(iti.total_product_tax_amount || 0),
      rate: Number(iti.tax_percentage || 0) / 100,
    };
  }
  return { amount: 0, rate: 0 };
};

export const getMarkerColors = (text: string, idx: number, palette: string[]) => {
  const t = (text || "").toLowerCase();

  if (t.includes("delivered")) {
    return { dot: "#00c951", halo: "rgba(34,197,94,.22)", bubble: "#EAF7F1" };
  }
  if (t.includes("shipped")) {
    return { dot: "#2b7fff", halo: "rgba(59,130,246,.22)", bubble: "#E8F1FF" };
  }
  if (t.includes("payment")) {
    return { dot: "#ff6900", halo: "rgba(245,158,11,.22)", bubble: "#FFF4E8" };
  }
  if (t.includes("pending") || t.includes("awaiting") || t.includes("net 30") || t.includes("net 40")) {
    return { dot: "#ff6900", halo: "rgba(245,158,11,.22)", bubble: "#FFF4E8" };
  }
  if (t.includes("confirm")) {
    return { dot: "#10B981", halo: "rgba(16,185,129,.22)", bubble: "#EAF7F1" };
  }
  if (t.includes("quote")) {
    return { dot: "#6366f1", halo: "rgba(99,102,241,.22)", bubble: "#EEF2FF" };
  }
  if (t.includes("invoice sent")) {
    return { dot: "#8b5cf6", halo: "rgba(139,92,246,.22)", bubble: "#F3E8FF" };
  }
  if (t.includes("cancel")) {
    return { dot: "#DC2626", halo: "rgba(220,38,38,.22)", bubble: "#FEE2E2" };
  }
  if (t.includes("created") || t.includes("placed")) {
    return { dot: "#6366F1", halo: "rgba(99,102,241,.22)", bubble: "#EEF2FF" };
  }
  const fallback = palette[idx % palette.length] || "#9CA3AF";
  return { dot: fallback, halo: "rgba(156,163,175,.22)", bubble: "#F3F4F6" };
};

export function renderShipping(order: any) {
  const method = String(order?.shipping_method?.service_name ?? order?.shipping?.method ?? "").trim();

  const amountRaw = order?.shipping_method?.cost ?? order?.shipping_amount ?? order?.shipping?.amount;

  const hasAmount = amountRaw !== undefined && amountRaw !== null && amountRaw !== "";
  const amount = Number(amountRaw);
  const hasMethod = method.length > 0;

  if (!hasMethod && !hasAmount) return "–";

  if (hasMethod && hasAmount && Number.isFinite(amount) && amount === 0) {
    return "Free Shipping";
  }

  if (!hasMethod) return "–";

  const price = Number.isFinite(amount) ? ` $${amount.toFixed(2)}` : "";
  return `${method}${price}`;
}

export type CustomInfoPair = { key: string; value: string };

/** Parse `custom_information` (e.g. "agentname:ramu,\nagentphone:123") into label/value rows. */
export function parseCustomInformation(info: string | undefined | null): CustomInfoPair[] {
  const raw = String(info ?? "").trim();
  if (!raw) return [];

  return raw
    .split(/\r?\n|,/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((part) => {
      const colonIdx = part.indexOf(":");
      if (colonIdx < 0) return { key: part, value: "" };
      return {
        key: part.slice(0, colonIdx).trim(),
        value: part.slice(colonIdx + 1).trim(),
      };
    })
    .filter(({ key, value }) => key.length > 0 || value.length > 0);
}

export function getOrderCartId(order: any): string | null {
  if (!order) return null;
  const candidates = [
    order?.cart_ids,
    order?.cart_id,
    order?.cartId,
    order?.order_tax_summary?.cart_ids,
    order?.order_tax_summary?.cart_id,
    order?.order_tax_summary?.cartId,
    order?.order_tax_summary?.taxcloud_cart_id,
    order?.order_tax_summary?.taxCloudCartId,
  ];

  for (const candidate of candidates) {
    if (candidate == null) continue;
    const str = String(candidate).trim();
    if (str.length) return str;
  }

  return null;
}

export type SalesOrderCustomerDetailsView = {
  forValue: string;
  brokerageVal: string;
  officeVal: string;
  /** Label for the agent / team-member row ("Agent" | "Team Member"). */
  personRoleLabel: string;
  agentVal: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  billingFirstName: string;
  billingLastName: string;
  shippingFirstName: string;
  shippingLastName: string;
};

function normalizeBuyerType(buyerType: unknown): string {
  return String(buyerType ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
}

function displayOrFallback(value: unknown, fallback: string): string {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function pickDisplayName(...candidates: unknown[]): string {
  return (
    candidates
      .map((v) => String(v ?? "").trim())
      .find(Boolean) || ""
  );
}

/** Resolve customer-details card values from legacy + foundation order fields. */
export function resolveSalesOrderCustomerDetails(order: any): SalesOrderCustomerDetailsView {
  const empty: SalesOrderCustomerDetailsView = {
    forValue: "--",
    brokerageVal: "--",
    officeVal: "--",
    personRoleLabel: "Agent",
    agentVal: "--",
    customerFirstName: "-",
    customerLastName: "-",
    customerEmail: "-",
    customerPhone: "-",
    billingFirstName: "-",
    billingLastName: "-",
    shippingFirstName: "-",
    shippingLastName: "-",
  };
  if (!order) return empty;

  const buyerType = normalizeBuyerType(order.buyer_type);
  const customerFullName = [order.customer_first_name, order.customer_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  const hasBrokerage = Boolean(
    order.brokerage_firm_id ||
      order.brokerage_firm_org_id ||
      order.brokerage_org_id ||
      buyerType === "brokeragefirm"
  );
  const hasOffice = Boolean(
    (order.office && (order.office._id || order.office.officeName)) ||
      order.office_membership_id ||
      order.office_org_id ||
      buyerType === "office"
  );
  const hasAgent = Boolean(
    order.agent_id ||
      order.agent_membership_id ||
      order.agent_org_id ||
      String(order.agent_name || "").trim() ||
      buyerType === "agent"
  );
  const hasTeamMember = Boolean(
    order.team_member_id ||
      order.team_member_membership_id ||
      order.team_member_org_id ||
      String(order.team_member_name || "").trim() ||
      buyerType === "teammember"
  );

  // When both agent + team member exist and the end user is the agent, prefer Team Member label/name.
  // When end user is teammember, always use Team Member (never mislabel as Agent).
  const preferTeamMemberLabel =
    buyerType === "teammember" ||
    (buyerType === "agent" && hasTeamMember) ||
    (hasTeamMember && !hasAgent);

  const personRoleLabel = preferTeamMemberLabel ? "Team Member" : "Agent";

  const agentVal =
    (preferTeamMemberLabel
      ? pickDisplayName(order.team_member_name, customerFullName, order.agent_name)
      : pickDisplayName(order.agent_name, customerFullName, order.team_member_name)) ||
    "--";

  const brokerageVal = hasBrokerage
    ? displayOrFallback(order.brokerage_firm_name || order.brokerage_org_name, "--")
    : "--";
  const officeVal = hasOffice
    ? displayOrFallback(order.office?.officeName || order.office_name, "--")
    : "--";

  let forValue = "--";
  if (hasBrokerage && hasOffice) {
    forValue = "Office";
  } else if (buyerType === "teammember" || (hasTeamMember && !hasAgent)) {
    forValue = "Team Member";
  } else if (hasBrokerage && (hasAgent || buyerType === "agent")) {
    forValue = "Agent";
  } else if (hasOffice || buyerType === "office") {
    forValue = "Office";
  } else if (hasAgent || buyerType === "agent") {
    forValue = "Agent";
  } else if (hasTeamMember) {
    forValue = "Team Member";
  } else if (hasBrokerage || buyerType === "brokeragefirm") {
    forValue = "Brokerage";
  } else {
    forValue = displayOrFallback(customerFullName, "--");
  }

  const billing = order.billing_address;
  const shipping = order.shipping_address;

  return {
    forValue,
    brokerageVal,
    officeVal,
    personRoleLabel,
    agentVal,
    customerFirstName: displayOrFallback(order.customer_first_name, "-"),
    customerLastName: displayOrFallback(order.customer_last_name, "-"),
    customerEmail: displayOrFallback(order.customer_email, "-"),
    customerPhone: displayOrFallback(order.customer_phone, "-"),
    billingFirstName: displayOrFallback(
      billing?.first_name || order.customer_first_name,
      "-"
    ),
    billingLastName: displayOrFallback(
      billing?.last_name || order.customer_last_name,
      "-"
    ),
    shippingFirstName: displayOrFallback(
      shipping?.first_name || order.customer_first_name,
      "-"
    ),
    shippingLastName: displayOrFallback(
      shipping?.last_name || order.customer_last_name,
      "-"
    ),
  };
}

/** Signage + taxcloud: block quote/payment/shipping until Get Live Taxes sets draft (or complete). */
export function salesOrderNeedsGetLiveTaxGate(
  activeTaxType: string,
  taxStatusRaw: unknown
): boolean {
  if (String(activeTaxType || "").trim().toLowerCase() !== "taxcloud") return false;
  const taxStatus = String(taxStatusRaw ?? "").trim().toLowerCase();
  return taxStatus !== "draft" && taxStatus !== "complete";
}
